import { useCallback, useState } from 'react';

/**
 * The unit the operator types and reads bulto weights in: kilos by default,
 * grams if they prefer.
 *
 * Display only. The backend stores and sends `weight_grams`, and the couriers
 * are quoted in grams; nothing here changes that. Whatever is typed in kilos
 * becomes grams before it leaves the form, and whatever comes back in grams is
 * shown in the chosen unit.
 *
 * The choice is a per-browser convenience, kept in localStorage like the other
 * ones; without storage it falls back to kilos every time.
 */
export const WEIGHT_UNITS = { KG: 'kg', G: 'g' };
export const DEFAULT_WEIGHT_UNIT = WEIGHT_UNITS.KG;

const STORAGE_KEY = 'bultoWeightUnit';

export const readWeightUnit = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored === WEIGHT_UNITS.G ? WEIGHT_UNITS.G : DEFAULT_WEIGHT_UNIT;
  } catch (e) {
    return DEFAULT_WEIGHT_UNIT;
  }
};

export const saveWeightUnit = (unit) => {
  try {
    localStorage.setItem(STORAGE_KEY, unit);
  } catch (e) {
    // Private window or blocked storage: the choice lasts the session only.
  }
};

/** The stored unit as React state, written back on every change. */
export const useWeightUnit = () => {
  const [unit, setUnitState] = useState(readWeightUnit);
  const setUnit = useCallback((next) => {
    const value = next === WEIGHT_UNITS.G ? WEIGHT_UNITS.G : WEIGHT_UNITS.KG;
    saveWeightUnit(value);
    setUnitState(value);
  }, []);
  return [unit, setUnit];
};

/**
 * Grams, as the backend keeps them, to the number the field shows.
 *
 * @param {number|null|undefined} grams
 * @param {'kg'|'g'} unit
 * @returns {number|null}
 */
export const gramsToUnit = (grams, unit) => {
  if (grams === null || grams === undefined || grams === '') return null;
  const value = Number(grams);
  if (!Number.isFinite(value)) return null;
  // Three decimals: a gram is the smallest thing the backend stores.
  return unit === WEIGHT_UNITS.KG ? Math.round(value) / 1000 : Math.round(value);
};

/**
 * What the field holds, in the chosen unit, to the integer grams the backend
 * takes. Accepts a decimal comma, which is how a Chilean keyboard types 0,5.
 *
 * @param {string|number|null|undefined} value
 * @param {'kg'|'g'} unit
 * @returns {number|null} Null when the field is empty or not a number.
 */
export const unitToGrams = (value, unit) => {
  if (value === null || value === undefined || value === '') return null;
  const number = parseFloat(String(value).replace(',', '.'));
  if (!Number.isFinite(number)) return null;
  return unit === WEIGHT_UNITS.KG ? Math.round(number * 1000) : Math.round(number);
};

/**
 * The same quantity re-typed in another unit, for a toggle that must not
 * lose what was already entered: "0.5" in kilos becomes "500" in grams.
 *
 * @param {string} value - What the field holds.
 * @param {'kg'|'g'} from
 * @param {'kg'|'g'} to
 * @returns {string}
 */
export const convertWeightInput = (value, from, to) => {
  if (from === to) return value;
  const grams = unitToGrams(value, from);
  if (grams === null) return '';
  return String(gramsToUnit(grams, to));
};

/**
 * A stored weight as text, in the chosen unit: "500 g" or "0,5 kg".
 *
 * @param {number|null|undefined} grams
 * @param {'kg'|'g'} unit
 * @returns {string} An em dash when there is no weight.
 */
export const formatWeight = (grams, unit) => {
  if (!grams) return '—';
  if (unit === WEIGHT_UNITS.KG) {
    return `${(grams / 1000).toLocaleString('es-CL', { maximumFractionDigits: 3 })} kg`;
  }
  return `${Number(grams).toLocaleString('es-CL')} g`;
};

/** The weight field's label and the step the browser's arrows move it by. */
export const weightFieldProps = (unit) =>
  unit === WEIGHT_UNITS.KG
    ? { label: 'Peso (kg)', step: '0.001', min: '0.001' }
    : { label: 'Peso (g)', step: '1', min: '1' };
